import { useSubscription } from '@apollo/client';
import { useNavigationStepData, useClientQueryWithSimpleErrorHandling, useToast } from 'hooks';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { makeReference } from '@apollo/client';
import { invalidateApolloClientCache } from 'hooks/apollo/common';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { QUOTATIONS_QUERY } from '../../../../queries';
import { COMPUTE_QUOTATIONS_FAILED, MARKED_AWAITING_CLIENT_RESPONSE } from '../../../../subscriptions';
import {
  QUOTATION_INITIALIZED,
  QUOTATION_COMPUTED,
  QUOTATIONS_INITIALIZED,
  RCA_QUOTATION_DATA_CHANGED
} from 'features/insuranceDocument/edit/offer/comparator/subscriptions';
import { DOCUMENT_NAVIGATION_STEP_CHANGED } from 'features/insuranceDocument/subscriptions/insuranceDocumentSubscriptions';
import PolicyRenewalItemQuotationsComponent from './PolicyRenewalItemQuotationsComponent';
import { emptyString } from 'constants/defaultValues';

const PolicyRenewalItemQuotationsContainer = ({ item, updateAuxiliaryInfo }) => {
  const {
    documentId: renewalDocumentId,
    renewalPolicyDocument: { documentId, quotationsDate, sentToClientDate }
  } = item;

  const { t, i18n } = useTranslation();
  const addToast = useToast();
  const { setNavigationStep } = useNavigationStepData();
  const clientQuery = useClientQueryWithSimpleErrorHandling();
  const client = useApolloClient();

  const [header, setHeader] = useState({});
  const [fetching, setFetching] = useState(true);

  const [quotations, setQuotations] = useState([]);

  useEffect(() => {
    setHeader({
      documentId,
      quotationsDate,
      sentToClientDate
    });
  }, [documentId, quotationsDate, sentToClientDate]);

  // #region Quotation processing

  const getDetailedQuotation = useCallback(
    (quotation, quotations) => ({
      ...quotation,
      insuranceAmount: quotation.details.find(y => !y.isDirectCompensation)?.insuranceAmount,
      additionalInfo: quotation.additionalInfo || quotations.find(y => y.offerId === quotation.sourceOfferId)?.additionalInfo,
      initialAmount: quotations.find(y => y.offerId === quotation.sourceOfferId)?.details?.find(y => !y.isDirectCompensation)
        ?.insuranceAmount
    }),
    []
  );

  const processQuotations = useCallback(
    inputQuotations => {
      if (!Array.isArray(inputQuotations) || inputQuotations.length === 0) return [];

      const quotations = [...inputQuotations];

      /* sort quotations */
      quotations.sort((q1, q2) => {
        const sortFn = (val1, val2) => (val1 === val2 ? 0 : val1 < val2 ? -1 : 1);
        return sortFn(q1.insurerName, q2.insurerName) || sortFn(q1.period, q2.period);
      });

      /* map quotations */
      return quotations.filter(x => !quotations.some(y => y.sourceOfferId === x.offerId)).map(x => getDetailedQuotation(x, quotations));
    },
    [getDetailedQuotation]
  );

  useEffect(() => {
    if (!documentId) return;
    async function loadQuotations() {
      const { data } = await clientQuery(QUOTATIONS_QUERY, {
        variables: { documentId },
        notifyOnNetworkStatusChange: true
      });

      const quotations = [...(data?.quotations?.quotations || [])];
      setQuotations(processQuotations(quotations));
      setFetching(false);
    }
    loadQuotations();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, processQuotations]);

  // #endregion

  // #region Subscriptions

  useSubscription(QUOTATION_COMPUTED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { quotationComputed }
      }
    }) => {
      if (!quotationComputed?.quotation) return;
      setQuotations(prev =>
        prev.map(x => (x.offerId === quotationComputed.quotation.offerId ? getDetailedQuotation(quotationComputed.quotation, prev) : x))
      );
    }
  });

  useSubscription(QUOTATION_INITIALIZED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { quotationInitialized }
      }
    }) => {
      if (!quotationInitialized?.quotation) return;
      setQuotations(prev =>
        prev.map(x =>
          x.offerId === quotationInitialized.quotation.offerId ? getDetailedQuotation(quotationInitialized.quotation, prev) : x
        )
      );
    }
  });

  useSubscription(QUOTATIONS_INITIALIZED, {
    variables: { renewalDocumentId },
    skip: !renewalDocumentId,
    onData: ({
      data: {
        data: { quotationsInitialized }
      }
    }) => {
      setHeader(prev => ({
        ...prev,
        quotationsDate: quotationsInitialized.quotationData.quotationsDate
      }));
      updateAuxiliaryInfo(emptyString);
      setQuotations(processQuotations(quotationsInitialized.quotationData.quotations));
    }
  });

  useSubscription(COMPUTE_QUOTATIONS_FAILED, {
    variables: { renewalDocumentId },
    skip: !renewalDocumentId,
    onData: ({
      data: {
        data: { computeQuotationsFailed }
      }
    }) => {
      const message = getCompositeMessage(computeQuotationsFailed);
      updateAuxiliaryInfo(message);
    }
  });

  useSubscription(RCA_QUOTATION_DATA_CHANGED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { rcaQuotationDataChanged }
      }
    }) => {
      addToast(t('Offer.PolicyRenewalList.Actions.UpdatedOffers'), 'success', { toastId: 'rcaQuotationDataChangedToast' });
      setQuotations(processQuotations(rcaQuotationDataChanged.quotationData.quotations));
    }
  });

  useSubscription(DOCUMENT_NAVIGATION_STEP_CHANGED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { documentNavigationStepChanged }
      }
    }) => {
      setNavigationStep(documentNavigationStepChanged.documentId, documentNavigationStepChanged.navigationStepId);
    }
  });

  useSubscription(MARKED_AWAITING_CLIENT_RESPONSE, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { markedAwaitingClientResponse }
      }
    }) => {
      invalidateApolloClientCache(client, {
        __typename: 'DocumentHeader',
        documentId: markedAwaitingClientResponse.documentId
      });
      invalidateApolloClientCache(client, makeReference('ROOT_QUERY'), [
        `documentAllowedTransitions({"documentId":${markedAwaitingClientResponse.documentId}})`
      ]);
      setHeader(prev => ({
        ...prev,
        sentToClientDate: markedAwaitingClientResponse.sentToClientDate
      }));
    }
  });

  // #endregion

  // #region Utils

  const getCompositeMessage = useCallback(
    data => {
      if (!data) return null;
      if (!data.isComposite) return data.message;
      if (data.message && i18n.exists(data.message)) return t(data.message, data.payload);
      return null;
    },
    [t, i18n]
  );

  // #endregion

  return <PolicyRenewalItemQuotationsComponent quotations={quotations} header={header} fetching={fetching} />;
};

PolicyRenewalItemQuotationsContainer.propTypes = {
  item: PropTypes.object.isRequired,
  updateAuxiliaryInfo: PropTypes.func
};
export default PolicyRenewalItemQuotationsContainer;
